import { useCallback, useMemo, useState } from 'react';
import { PaginationState } from 'src/components/common/TablePagination';

/**
 * URL-backed sibling of `usePaginationState`. Exposes the same interface (so
 * `PaginatedGentlyTable` is a drop-in), but page / pageSize / search / sort are
 * sourced from the route's search params instead of local React state — so
 * filter + paging state survives reloads, is shareable, and round-trips through
 * the URL.
 *
 * Typing for route-relative navigation lives in the page (which knows its route
 * path literal): the page reads `search` via `useSearch({ from })` and passes a
 * typed `updateSearch` that writes back via `navigate({ to, search })`. This
 * hook is pure pagination logic on top of that. `setPaginationInfo` /
 * `setHasNextPage` stay local — they're derived from the server response, not
 * user navigation.
 */
export type UrlPaginationParams = {
  page?: number;
  pageSize?: number;
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
};

interface UseUrlPaginationStateProps {
  /** Current route search params (already validated by the route). */
  search: UrlPaginationParams;
  /** Merge a patch into the route search params (page-owned, typed navigate). */
  updateSearch: (patch: UrlPaginationParams) => void;
  initialPageSize?: number;
  defaultSortBy?: string;
}

export function useUrlPaginationState({
  search,
  updateSearch,
  initialPageSize = 25,
  defaultSortBy = '',
}: UseUrlPaginationStateProps) {
  const pageSize = search.pageSize ?? initialPageSize;
  const currentPage = search.page ?? 1;
  const searchQuery = search.search ?? '';
  const sortBy = search.sortBy ?? defaultSortBy;
  const sortDirection: 'ASC' | 'DESC' =
    search.sortDirection === 'desc' ? 'DESC' : 'ASC';

  // Total/hasNext come from the fetched page, not the URL — keep them local.
  const [hasNextPage, setHasNextPageState] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const setPageSize = useCallback(
    (size: number) => updateSearch({ pageSize: size, page: undefined }),
    [updateSearch],
  );

  const setSearchQuery = useCallback(
    (query: string) =>
      updateSearch({ search: query || undefined, page: undefined }),
    [updateSearch],
  );

  const setSorting = useCallback(
    (newSortBy: string, newSortDirection: 'ASC' | 'DESC') =>
      updateSearch({
        sortBy: newSortBy,
        sortDirection: newSortDirection === 'ASC' ? 'asc' : 'desc',
        page: undefined,
      }),
    [updateSearch],
  );

  const goToNextPage = useCallback(() => {
    if (hasNextPage) updateSearch({ page: currentPage + 1 });
  }, [hasNextPage, currentPage, updateSearch]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) updateSearch({ page: currentPage - 1 });
  }, [currentPage, updateSearch]);

  const goToFirstPage = useCallback(
    () => updateSearch({ page: undefined }),
    [updateSearch],
  );

  const goToPage = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) updateSearch({ page });
    },
    [totalPages, updateSearch],
  );

  const reset = useCallback(
    () =>
      updateSearch({
        page: undefined,
        pageSize: undefined,
        search: undefined,
        sortBy: undefined,
        sortDirection: undefined,
      }),
    [updateSearch],
  );

  const getCurrentOffset = useCallback(
    () => (currentPage - 1) * pageSize,
    [currentPage, pageSize],
  );

  const setPaginationInfo = useCallback(
    (info: { totalCount: number; totalPages: number; currentPage: number }) => {
      setTotalCount(info.totalCount);
      setTotalPages(info.totalPages);
    },
    [],
  );

  const setHasNextPage = useCallback(
    (hasNext: boolean) => setHasNextPageState(hasNext),
    [],
  );

  const paginationState: PaginationState = useMemo(() => {
    const startItem = totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endItem = Math.min(currentPage * pageSize, totalCount);
    return {
      pageSize,
      currentPage,
      hasNextPage,
      hasPreviousPage: currentPage > 1,
      totalItems: totalCount,
      startItem,
      endItem,
    };
  }, [pageSize, currentPage, hasNextPage, totalCount]);

  return {
    pageSize,
    currentPage,
    searchQuery,
    sortBy,
    sortDirection,
    setPageSize,
    setSearchQuery,
    setSorting,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToPage,
    reset,
    paginationState,
    getCurrentOffset,
    setHasNextPage,
    setPaginationInfo,
  };
}
